import { useDeferredValue, useState, type FormEvent } from "react";
import {
  useCreateReference,
  useReferences,
  useRestoreReference,
  useSpaceParam,
  useTrashReference,
  useUpdateReference,
} from "./hooks";
import type { Reference, ReferenceInput } from "./types";

/** Wiedza do wglądu: wyszukiwarka, dopisywanie, edycja i kosz z cofnięciem. */
export function ReferencesView() {
  const space = useSpaceParam();
  const [query, setQuery] = useState("");
  const q = useDeferredValue(query.trim());
  const { data, isLoading, error } = useReferences({ q: q || undefined, space, limit: 200 });
  const create = useCreateReference();
  const trash = useTrashReference();
  const restore = useRestoreReference();
  const [showForm, setShowForm] = useState(false);
  const [lastTrashed, setLastTrashed] = useState<Reference | null>(null);

  return (
    <div className="view">
      <input
        className="field__input"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Szukaj w tytułach, treści i tematach…"
        aria-label="Szukaj w materiałach"
      />
      <button className="btn btn--primary btn--block" onClick={() => setShowForm(!showForm)}>
        {showForm ? "Anuluj" : "+ Nowy materiał"}
      </button>

      {showForm && (
        <ReferenceForm
          busy={create.isPending}
          error={create.error}
          submitLabel="Zapisz"
          onSubmit={(input) =>
            create.mutate({ ...input, space: space ?? null }, { onSuccess: () => setShowForm(false) })
          }
        />
      )}

      {lastTrashed && (
        <p className="hint" role="status">
          <span>Wyrzucono „{lastTrashed.title}”.</span>
          <span className="hint__actions">
            <button
              className="btn btn--small"
              onClick={() =>
                restore.mutate(lastTrashed.id, { onSuccess: () => setLastTrashed(null) })
              }
            >
              Cofnij
            </button>
            <button className="btn btn--small" onClick={() => setLastTrashed(null)}>
              OK
            </button>
          </span>
        </p>
      )}

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać materiałów.</p>}
      {data && data.items.length === 0 && (
        <p className="muted">{q ? "Nic nie pasuje do tego wyszukiwania." : "Brak materiałów."}</p>
      )}

      <ul className="rows">
        {data?.items.map((item) => (
          <ReferenceRow
            key={item.id}
            item={item}
            onTrash={() => trash.mutate(item.id, { onSuccess: () => setLastTrashed(item) })}
          />
        ))}
      </ul>
      {trash.error && <p className="error">{(trash.error as Error).message}</p>}
    </div>
  );
}

export function ReferenceRow({ item, onTrash }: { item: Reference; onTrash: () => void }) {
  const update = useUpdateReference();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <li className="row row--block">
      <div className="row__head" onClick={() => setOpen(!open)} role="button" tabIndex={0}>
        <div className="row__main">
          <span className="row__title">{item.title}</span>
          {item.topic && (
            <span className="row__meta">
              <span>{item.topic}</span>
            </span>
          )}
        </div>
        <span className="row__chevron">{open ? "▾" : "▸"}</span>
      </div>

      {open && (
        <div className="drawer">
          {editing ? (
            <ReferenceForm
              initial={item}
              busy={update.isPending}
              error={update.error}
              submitLabel="Zapisz"
              onCancel={() => setEditing(false)}
              onSubmit={(input) =>
                update.mutate({ id: item.id, input }, { onSuccess: () => setEditing(false) })
              }
            />
          ) : (
            <>
              {item.content && item.content !== item.title && (
                <p className="reference-text">{item.content}</p>
              )}
              <div className="project-actions">
                <button className="btn" onClick={() => setEditing(true)}>
                  Edytuj
                </button>
                <button className="btn btn--danger" onClick={onTrash}>
                  Do kosza
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function ReferenceForm({
  initial,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Reference;
  busy: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (input: ReferenceInput) => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const [topic, setTopic] = useState(initial?.topic ?? "");
  const invalid = initial ? !title.trim() : !title.trim() && !content.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (invalid) return;
    onSubmit({
      title: title.trim() || null,
      content: content.trim() || null,
      topic: topic.trim() || null,
    });
  }

  return (
    <form className="project-edit" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Tytuł{initial ? "" : " (puste = pierwsza linia treści)"}</span>
        <input
          className="field__input"
          value={title}
          maxLength={512}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Temat</span>
        <input
          className="field__input"
          value={topic}
          maxLength={128}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="np. Dom, Finanse"
        />
      </label>
      <label className="field">
        <span className="field__label">Treść</span>
        <textarea
          className="field__input"
          rows={5}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>
      <div className="clarify__buttons">
        <button className="btn btn--primary" type="submit" disabled={invalid || busy}>
          {submitLabel}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>
            Anuluj
          </button>
        )}
      </div>
      {Boolean(error) && <p className="error">{(error as Error).message}</p>}
    </form>
  );
}
